import { StyleSheet, css } from 'aphrodite';
import { type FC, type MouseEvent, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { copyToClipboard } from '../../../models/utils/uiUtils';
import { Button } from '../../../web/components-v2/Button';
import {
	Dropdown,
	DropdownContent,
	DropdownEllipsisTrigger,
	DropdownItem,
	DropdownSeparator,
} from '../../../web/components-v2/Dropdown';
import { Input } from '../../../web/components-v2/Input';
import type { FocusedLead, FocusedLeadContact, FocusedLeadContactPhone } from '../data';
import {
	IconChevronRight,
	IconClock,
	IconCopy,
	IconGlobe,
	IconHubSpot,
	IconLinkedIn,
	IconMail,
	IconMapPin,
	IconPhone,
	IconPlus,
	IconSearch,
	IconTwitter,
} from './icons';

/**
 * Left column of the Focused Lead View.
 *
 * Pure prop-driven lead info panel: company, website, location/timezone,
 * social icons, last contact, CRM tags, and expandable contact rows.
 * Consumes `FocusedLead` from `../data`; gracefully omits optional sections
 * when their backing fields are not populated.
 *
 * Visual spec mirrors `/tmp/design-bundle/focused-view/project/LeftColumn.jsx`.
 * Styling uses Aphrodite with theme tokens (`var(--ink)`, `var(--border)`,
 * etc.) so light/dark mode is handled automatically by `theme/tokens.css`.
 */
type LeftColumnProps = {
	lead: FocusedLead;
	/** Open the Add Contact dialog. */
	onAddContact?: () => void;
	onEditContact?: (contact: FocusedLeadContact) => void;
	onToggleDecisionMaker?: (contact: FocusedLeadContact) => void;
	onDeleteContact?: (contact: FocusedLeadContact) => void;
	/** Place an outbound call to a specific phone number on a contact. */
	onCallContact?: (contact: FocusedLeadContact, phone: FocusedLeadContactPhone) => void;
	/** Trigger the cell-phone enrichment lookup for a contact. */
	onFindCellPhone?: (contact: FocusedLeadContact) => void;
	/** True while a cell-phone lookup is in flight (any contact). */
	findCellPhonePendingId?: string | null;
	/** Open the Edit Company Details dialog. */
	onEditCompany?: () => void;
	/** Place an outbound call to a lead-level (company) phone number. */
	onCallCompanyPhone?: (phone: FocusedLeadContactPhone) => void;
	/** Open the company phone-numbers manager dialog. */
	onManageCompanyPhones?: () => void;
};

/**
 * Pick the lead's HubSpot deep-link out of `relatedLinks`. Matches the legacy
 * `CallingCard` pattern of relying on the backend-supplied label string.
 * Returns undefined when the account doesn't have a HubSpot integration.
 */
function findHubSpotLink(lead: FocusedLead): string | undefined {
	return lead.relatedLinks?.find(link => /hubspot/i.test(link.label))?.url;
}

function buildLeadShareUrl(companyId: string): string {
	const { origin, pathname } = window.location;
	return `${origin}${pathname}#/focused-view?from=SharedLink&companyId=${encodeURIComponent(companyId)}`;
}

export const LeftColumn: FC<LeftColumnProps> = ({
	lead,
	onAddContact,
	onEditContact,
	onToggleDecisionMaker,
	onDeleteContact,
	onCallContact,
	onFindCellPhone,
	findCellPhonePendingId,
	onEditCompany,
	onCallCompanyPhone,
	onManageCompanyPhones,
}) => {
	const hubspotUrl = findHubSpotLink(lead);
	const handleOpenHubSpot = () => {
		if (!hubspotUrl) return;
		window.open(hubspotUrl, '_blank', 'noopener,noreferrer');
	};
	const handleCopyLeadLink = () => {
		if (!lead.companyId) return;
		const url = buildLeadShareUrl(lead.companyId);
		void copyToClipboard(url).then(success => {
			if (success) toast.success('Lead link copied to clipboard');
			else toast.error('Failed to copy lead link');
		});
	};
	const hasStageBadges = Boolean(lead.dealType || lead.dealStage || lead.outcome);
	const hasLocationRow = Boolean(lead.location || lead.timezone);
	const [contactsFilter, setContactsFilter] = useState('');

	const filteredContacts = useMemo(() => {
		const q = contactsFilter.trim().toLowerCase();
		if (!q) return lead.contacts;
		return lead.contacts.filter(c => {
			const phoneHaystack = c.phones.map(p => `${p.label ?? ''} ${p.standard} ${p.display}`).join(' ');
			const haystack = [c.name, c.title, c.email, phoneHaystack].filter(Boolean).join(' ').toLowerCase();
			return haystack.includes(q);
		});
	}, [contactsFilter, lead.contacts]);

	return (
		<div className={css(styles.col, styles.colLeft)}>
			<div className={css(styles.colHead)}>
				<div className={css(styles.colTitle)}>Lead</div>
				<div className={css(styles.headActions)}>
					{hubspotUrl && (
						<Button
							variant='ghost'
							size='icon'
							title='Open in HubSpot'
							aria-label='Open in HubSpot'
							onClick={handleOpenHubSpot}
						>
							<IconHubSpot />
						</Button>
					)}
					<Dropdown>
						<DropdownEllipsisTrigger />
						<DropdownContent align='end'>
							<DropdownItem onClick={() => onEditCompany?.()} disabled={!onEditCompany}>
								Edit company details
							</DropdownItem>
							<DropdownItem onClick={handleCopyLeadLink} disabled={!lead.companyId}>
								Copy link to lead
							</DropdownItem>
						</DropdownContent>
					</Dropdown>
				</div>
			</div>

			<div className={css(styles.colBody)}>
				{hasStageBadges && (
					<div className={css(styles.stageBadges)}>
						{lead.dealType && (
							<span className={css(styles.pill, styles.pillSolid)}>
								<span className={css(styles.dot)} style={{ background: '#10A37F' }} />
								{lead.dealType}
							</span>
						)}
						{lead.dealStage && <span className={css(styles.pill)}>{lead.dealStage}</span>}
						{lead.outcome && <span className={css(styles.pill, styles.pillAccent)}>{lead.outcome}</span>}
					</div>
				)}

				<div className={css(styles.companyWrap)}>
					<h1 className={css(styles.companyName)}>{lead.company}</h1>
					{lead.companyId && (
						<button
							type='button'
							className={css(styles.companyCopyBtn)}
							title='Copy link to lead'
							onClick={handleCopyLeadLink}
						>
							<IconCopy size={14} />
						</button>
					)}
				</div>

				{lead.website && (
					<a href={lead.websiteUrl ?? lead.website} className={css(styles.websiteLink)}>
						<IconGlobe size={13} />
						<span>{lead.website}</span>
					</a>
				)}

				{hasLocationRow && (
					<div className={css(styles.locationRow)}>
						{lead.location && (
							<>
								<IconMapPin size={13} />
								<span>{lead.location}</span>
							</>
						)}
						{lead.location && lead.timezone && <span className={css(styles.locationSep)}>·</span>}
						{lead.timezone && (
							<>
								<IconClock size={13} />
								<span>{lead.timezone}</span>
							</>
						)}
					</div>
				)}

				{(lead.socialProfiles.linkedIn || lead.socialProfiles.twitter) && (
					<div className={css(styles.socialRow)}>
						{lead.socialProfiles.linkedIn && (
							<a
								href={lead.socialProfiles.linkedIn}
								target='_blank'
								rel='noreferrer noopener'
								title='LinkedIn'
								aria-label='Open LinkedIn profile in a new tab'
								className={css(styles.socialLink)}
							>
								<IconLinkedIn size={14} />
							</a>
						)}
						{lead.socialProfiles.twitter && (
							<a
								href={lead.socialProfiles.twitter}
								target='_blank'
								rel='noreferrer noopener'
								title='X / Twitter'
								aria-label='Open X/Twitter profile in a new tab'
								className={css(styles.socialLink)}
							>
								<IconTwitter size={13} />
							</a>
						)}
					</div>
				)}

				<hr className={css(styles.hr)} />
				<div className={css(styles.companyPhonesHeader)}>
					<div className={css(styles.colTitle)}>Phone numbers</div>
					{onManageCompanyPhones && (
						<Button variant='ghost' size='sm' onClick={onManageCompanyPhones}>
							Manage
						</Button>
					)}
				</div>
				{(lead.companyPhones?.length ?? 0) > 0 ? (
					<div className={css(styles.companyPhonesList)}>
						{(lead.companyPhones ?? []).map((phone, i) => (
							<div key={`${phone.standard}-${i}`} className={css(styles.companyPhoneRow)}>
								<div className={css(styles.companyPhoneInfo)}>
									{phone.label && <span className={css(styles.detailLabel)}>{phone.label}: </span>}
									<span className={css(styles.detailValue)}>{phone.display}</span>
									{phone.extension && !phone.isInternational && (
										<span className={css(styles.detailValue)}> ext {phone.extension}</span>
									)}
								</div>
								<button
									type='button'
									className={css(styles.copyBtn)}
									title='Copy phone number'
									onClick={() => copyWithToast('Phone number', phone.standard)}
								>
									<IconCopy size={12} />
								</button>
								<button
									type='button'
									className={css(styles.companyPhoneCallBtn)}
									title={`Call ${phone.display}`}
									onClick={() => onCallCompanyPhone?.(phone)}
									disabled={!onCallCompanyPhone}
								>
									Call
								</button>
							</div>
						))}
					</div>
				) : (
					<div className={css(styles.companyPhonesEmpty)}>No numbers found.</div>
				)}

				{lead.lastContact && (
					<>
						<hr className={css(styles.hr)} />
						<div className={css(styles.lastContactRow)}>
							<div className={css(styles.lastContactIcon)}>
								<IconClock size={14} />
							</div>
							<div>
								<div className={css(styles.colTitle, styles.lastContactLabel)}>Last Contact</div>
								<div className={css(styles.lastContactValue)}>{lead.lastContact}</div>
							</div>
						</div>
					</>
				)}

				{lead.crmTags.length > 0 && (
					<>
						<hr className={css(styles.hr)} />
						<div className={css(styles.colTitle, styles.sectionLabel)}>Context</div>
						<div className={css(styles.tagsWrap)}>
							{lead.crmTags.map(tag => (
								<span key={`${tag.kind ?? ''}:${tag.label}`} className={css(styles.pill, styles.tagPill)}>
									{tag.kind && <span className={css(styles.tagKind)}>{tag.kind}</span>}
									{tag.label}
								</span>
							))}
						</div>
					</>
				)}

				<hr className={css(styles.hr)} />

				<div className={css(styles.contactsHeader)}>
					<div className={css(styles.contactsSearchWrap)}>
						<IconSearch size={13} sw={1.8} color='var(--ink-faint)' />
						<Input
							type='search'
							value={contactsFilter}
							onChange={e => setContactsFilter(e.target.value)}
							placeholder='Filter contacts'
							styles={[styles.contactsSearchInput]}
						/>
					</div>
					<Button variant='ghost' size='sm' title='Add contact' onClick={onAddContact} disabled={!onAddContact}>
						<IconPlus size={12} />
						Add
					</Button>
				</div>
				<div className={css(styles.contactsList)}>
					{filteredContacts.length === 0 && contactsFilter ? (
						<div className={css(styles.contactsEmpty)}>No contacts match &ldquo;{contactsFilter}&rdquo;</div>
					) : (
						filteredContacts.map(contact => (
							<ContactRow
								key={contact.id}
								contact={contact}
								onEdit={onEditContact}
								onToggleDecisionMaker={onToggleDecisionMaker}
								onDelete={onDeleteContact}
								onCall={onCallContact}
								onFindCellPhone={onFindCellPhone}
								findCellPhonePending={findCellPhonePendingId === contact.id}
							/>
						))
					)}
				</div>
			</div>
		</div>
	);
};

type ContactRowProps = {
	contact: FocusedLeadContact;
	onEdit?: (contact: FocusedLeadContact) => void;
	onToggleDecisionMaker?: (contact: FocusedLeadContact) => void;
	onDelete?: (contact: FocusedLeadContact) => void;
	onCall?: (contact: FocusedLeadContact, phone: FocusedLeadContactPhone) => void;
	onFindCellPhone?: (contact: FocusedLeadContact) => void;
	findCellPhonePending?: boolean;
};

const copyWithToast = (label: string, value: string) => {
	void copyToClipboard(value).then(success => {
		if (success) toast.success(`${label} copied to clipboard`);
		else toast.error(`${label} failed to copy to clipboard`);
	});
};

/**
 * Avatar for a contact row. Renders the profile image when one is available
 * and falls back to initials when not (or when the image fails to load).
 * Mirrors the legacy `<Avatar entity={c} />` behavior, but without pulling in
 * the MobX-based component.
 */
const ContactAvatar: FC<{ contact: FocusedLeadContact }> = ({ contact }) => {
	const [failed, setFailed] = useState(false);
	const showImage = !!contact.imageUrl && !failed;
	return (
		<div className={css(styles.avatar)}>
			{showImage ? (
				<img src={contact.imageUrl} alt='' className={css(styles.avatarImage)} onError={() => setFailed(true)} />
			) : (
				contact.initials
			)}
		</div>
	);
};

const ContactRow: FC<ContactRowProps> = ({
	contact,
	onEdit,
	onToggleDecisionMaker,
	onDelete,
	onCall,
	onFindCellPhone,
	findCellPhonePending,
}) => {
	const [expanded, setExpanded] = useState<boolean>(Boolean(contact.primary));
	const isDM = Boolean(contact.primary);
	const primaryPhone = contact.phones[0];
	const hasMobile = contact.phones.some(p => (p.label ?? '').toLowerCase().includes('mobile'));
	const showFindCellPhone = !hasMobile;

	const stopMenuClick = (e: MouseEvent<HTMLDivElement>) => {
		e.stopPropagation();
	};

	return (
		<div className={css(styles.contactRow)}>
			<div className={css(styles.contactRowMain)}>
				<button
					type='button'
					onClick={() => setExpanded(value => !value)}
					className={css(styles.contactHeaderBtn)}
					aria-expanded={expanded}
				>
					<div className={css(styles.contactHead)}>
						<ContactAvatar contact={contact} />
						<div className={css(styles.contactTextCol)}>
							<div className={css(styles.contactNameLine)}>
								<span className={css(styles.contactName)}>{contact.name}</span>
								<span className={css(styles.contactReachIcons)} aria-hidden>
									{contact.phones.length > 0 && (
										<span
											className={css(styles.contactReachIcon)}
											title={`${contact.phones.length} phone number${contact.phones.length === 1 ? '' : 's'}`}
										>
											<IconPhone size={11} />
										</span>
									)}
									{contact.email && (
										<span className={css(styles.contactReachIcon)} title='Has email'>
											<IconMail size={11} />
										</span>
									)}
								</span>
								{isDM && <span className={css(styles.dmBadge)}>DM</span>}
							</div>
							{contact.title && <div className={css(styles.contactTitle)}>{contact.title}</div>}
						</div>
					</div>
				</button>
				{expanded && (
					<div className={css(styles.contactDetails)} onClick={stopMenuClick}>
						<div className={css(styles.contactBodyRow)}>
							<div className={css(styles.contactDetailsCol)}>
								{contact.phones.map((phone, i) => (
									<div key={`${phone.standard}-${i}`} className={css(styles.detailLine)}>
										{phone.label && <span className={css(styles.detailLabel)}>{phone.label}: </span>}
										<span className={css(styles.detailValue)}>{phone.display}</span>
										{phone.extension && !phone.isInternational && (
											<span className={css(styles.detailValue)}> ext {phone.extension}</span>
										)}
										<button
											type='button'
											className={css(styles.copyBtn)}
											title='Copy phone number'
											onClick={() => copyWithToast('Phone number', phone.standard)}
										>
											<IconCopy size={12} />
										</button>
									</div>
								))}
								{showFindCellPhone && (
									<button
										type='button'
										className={css(styles.findCellLink)}
										onClick={() => onFindCellPhone?.(contact)}
										disabled={!onFindCellPhone || findCellPhonePending}
									>
										{findCellPhonePending ? 'Looking up cell phone…' : 'Find cell phone'}
									</button>
								)}
								{contact.emailAddresses.map(email => (
									<div key={email.value} className={css(styles.detailLine)}>
										<a href={`mailto:${email.value}`} className={css(styles.emailLink)}>
											{email.value}
										</a>
										{email.isUnsubscribed && (
											<span className={css(styles.unsubscribedBadge)} title='This contact is unsubscribed from email'>
												unsubscribed
											</span>
										)}
										<button
											type='button'
											className={css(styles.copyBtn)}
											title='Copy email'
											onClick={() => copyWithToast('Email', email.value)}
										>
											<IconCopy size={12} />
										</button>
									</div>
								))}
							</div>
							{primaryPhone && (
								<button
									type='button'
									className={css(styles.callBtn)}
									onClick={() => onCall?.(contact, primaryPhone)}
									disabled={!onCall}
									title={`Call ${primaryPhone.display}`}
								>
									Call
								</button>
							)}
						</div>
					</div>
				)}
			</div>

			<div className={css(styles.contactRightActions)} onClick={stopMenuClick}>
				<Dropdown>
					<DropdownEllipsisTrigger />
					<DropdownContent align='end'>
						<DropdownItem onClick={() => onEdit?.(contact)} disabled={!onEdit}>
							Edit
						</DropdownItem>
						<DropdownItem onClick={() => onToggleDecisionMaker?.(contact)} disabled={!onToggleDecisionMaker}>
							{isDM ? 'Unmark as decision maker' : 'Mark as decision maker'}
						</DropdownItem>
						<DropdownSeparator />
						<DropdownItem onClick={() => onDelete?.(contact)} disabled={!onDelete}>
							Delete
						</DropdownItem>
					</DropdownContent>
				</Dropdown>
				<div
					className={css(styles.chevron)}
					style={{ transform: expanded ? 'rotate(90deg)' : 'rotate(0)' }}
					aria-hidden
				>
					<IconChevronRight size={14} />
				</div>
			</div>
		</div>
	);
};

const styles = StyleSheet.create({
	col: {
		display: 'flex',
		flexDirection: 'column',
		minWidth: 0,
		minHeight: 0,
		height: '100vh',
	},
	colLeft: {
		borderRight: '1px solid var(--border)',
		background: 'var(--bg)',
	},
	colHead: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		height: 52,
		padding: '0 20px',
		borderBottom: '1px solid var(--border)',
		flexShrink: 0,
	},
	colTitle: {
		fontSize: 11,
		fontWeight: 600,
		letterSpacing: '0.08em',
		textTransform: 'uppercase',
		color: 'var(--ink-faint)',
	},
	headActions: {
		display: 'flex',
		gap: 2,
	},
	colBody: {
		flex: 1,
		overflowY: 'auto',
		minHeight: 0,
		padding: '24px 20px 32px',
	},
	iconBtn: {
		width: 30,
		height: 30,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: 8,
		color: 'var(--ink-muted)',
		background: 'transparent',
		border: 'none',
		cursor: 'pointer',
		padding: 0,
		transition: 'background 120ms, color 120ms',
		':hover': {
			background: 'var(--hover)',
			color: 'var(--ink)',
		},
	},
	stageBadges: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: 6,
		marginBottom: 20,
	},
	pill: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 6,
		height: 24,
		padding: '0 10px',
		borderRadius: 999,
		fontSize: 12,
		fontWeight: 500,
		background: 'var(--bg-sunken)',
		color: 'var(--ink)',
		border: '1px solid var(--border)',
	},
	pillSolid: {
		background: 'var(--ink)',
		color: 'var(--bg)',
		borderColor: 'transparent',
	},
	pillAccent: {
		background: 'var(--accent-soft)',
		color: 'var(--accent-ink)',
		borderColor: 'transparent',
	},
	dot: {
		width: 6,
		height: 6,
		borderRadius: 999,
		display: 'inline-block',
	},
	companyWrap: {
		marginBottom: 4,
		display: 'flex',
		alignItems: 'center',
		gap: 8,
	},
	companyCopyBtn: {
		width: 28,
		height: 28,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: 6,
		color: 'var(--ink-muted)',
		background: 'transparent',
		border: 'none',
		cursor: 'pointer',
		padding: 0,
		transition: 'background 120ms, color 120ms',
		':hover': {
			background: 'var(--hover)',
			color: 'var(--ink)',
		},
	},
	companyName: {
		fontSize: 32,
		fontWeight: 600,
		margin: 0,
		letterSpacing: '-0.02em',
		lineHeight: 1.1,
		color: 'var(--ink)',
	},
	websiteLink: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 6,
		color: 'var(--ink-muted)',
		fontSize: 13,
		marginTop: 8,
		textDecoration: 'none',
	},
	locationRow: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		color: 'var(--ink-muted)',
		fontSize: 13,
		marginTop: 6,
	},
	locationSep: {
		color: 'var(--ink-faint)',
	},
	socialRow: {
		display: 'flex',
		gap: 4,
		marginTop: 16,
	},
	socialLink: {
		width: 30,
		height: 30,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: 8,
		color: 'var(--ink-muted)',
		background: 'transparent',
		textDecoration: 'none',
		transition: 'background 120ms, color 120ms',
		':hover': {
			background: 'var(--hover)',
			color: 'var(--ink)',
		},
	},
	companyPhonesHeader: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 8,
		marginBottom: 8,
	},
	companyPhonesList: {
		display: 'flex',
		flexDirection: 'column',
		gap: 6,
	},
	companyPhoneRow: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		padding: '6px 8px',
		borderRadius: 8,
		background: 'var(--bg-elev)',
		border: '1px solid var(--border)',
	},
	companyPhoneInfo: {
		flex: 1,
		minWidth: 0,
		fontSize: 12.5,
		color: 'var(--ink)',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	companyPhoneCallBtn: {
		flexShrink: 0,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: 28,
		padding: '0 14px',
		borderRadius: 6,
		background: 'var(--accent)',
		color: 'var(--bg)',
		border: 'none',
		fontSize: 12,
		fontWeight: 600,
		cursor: 'pointer',
		transition: 'filter 120ms',
		':hover': {
			filter: 'brightness(1.05)',
		},
		':disabled': {
			background: 'var(--bg-sunken)',
			color: 'var(--ink-faint)',
			cursor: 'not-allowed',
		},
	},
	companyPhonesEmpty: {
		padding: '12px 14px',
		fontSize: 12.5,
		color: 'var(--ink-muted)',
		textAlign: 'center',
		background: 'var(--accent-soft)',
		borderRadius: 8,
	},
	hr: {
		height: 1,
		background: 'var(--border)',
		border: 'none',
		margin: '24px 0',
	},
	lastContactRow: {
		display: 'flex',
		alignItems: 'flex-start',
		gap: 10,
	},
	lastContactIcon: {
		width: 28,
		height: 28,
		borderRadius: 8,
		background: 'var(--bg-sunken)',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		color: 'var(--ink-muted)',
		flexShrink: 0,
	},
	lastContactLabel: {
		marginBottom: 2,
	},
	lastContactValue: {
		fontSize: 13,
		color: 'var(--ink)',
	},
	sectionLabel: {
		marginBottom: 10,
	},
	tagsWrap: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: 6,
	},
	tagPill: {
		fontSize: 11.5,
		height: 22,
		padding: '0 9px',
	},
	tagKind: {
		color: 'var(--ink-faint)',
		fontSize: 10,
		marginRight: 2,
	},
	contactsHeader: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		gap: 8,
		marginBottom: 10,
	},
	contactsSearchWrap: {
		flex: 1,
		minWidth: 0,
		display: 'flex',
		alignItems: 'center',
		gap: 6,
		padding: '0 8px',
		background: 'var(--bg-sunken)',
		border: '1px solid var(--border)',
		borderRadius: 8,
	},
	contactsSearchInput: {
		flex: 1,
		minWidth: 0,
		height: 28,
		padding: 0,
		border: 'none',
		background: 'transparent',
		fontSize: 12.5,
		color: 'var(--ink)',
	},
	contactsEmpty: {
		padding: '12px 8px',
		fontSize: 12,
		color: 'var(--ink-muted)',
		textAlign: 'center',
	},
	contactsList: {
		display: 'flex',
		flexDirection: 'column',
		gap: 2,
	},
	contactRow: {
		display: 'flex',
		alignItems: 'flex-start',
		gap: 4,
		borderRadius: 8,
		paddingRight: 6,
		transition: 'background 120ms',
		':hover': {
			background: 'var(--hover)',
		},
	},
	contactRowMain: {
		flex: 1,
		minWidth: 0,
		display: 'flex',
		flexDirection: 'column',
	},
	contactHeaderBtn: {
		width: '100%',
		textAlign: 'left',
		padding: '10px 8px',
		borderRadius: 8,
		cursor: 'pointer',
		background: 'transparent',
		border: 'none',
		color: 'inherit',
		font: 'inherit',
	},
	contactRightActions: {
		display: 'flex',
		alignItems: 'center',
		gap: 2,
		paddingTop: 12,
		flexShrink: 0,
	},
	contactHead: {
		display: 'flex',
		alignItems: 'center',
		gap: 10,
	},
	avatar: {
		width: 32,
		height: 32,
		borderRadius: 999,
		background: 'var(--bg-sunken)',
		border: '1px solid var(--border)',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		fontSize: 11,
		fontWeight: 600,
		color: 'var(--ink-muted)',
		flexShrink: 0,
		overflow: 'hidden',
	},
	avatarImage: {
		width: '100%',
		height: '100%',
		objectFit: 'cover',
	},
	contactTextCol: {
		minWidth: 0,
		flex: 1,
	},
	contactNameLine: {
		display: 'flex',
		alignItems: 'center',
		gap: 6,
		fontSize: 13,
		fontWeight: 500,
		color: 'var(--ink)',
	},
	contactName: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	contactReachIcons: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 3,
		color: 'var(--ink-faint)',
		flexShrink: 0,
	},
	contactReachIcon: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	dmBadge: {
		fontSize: 10,
		fontWeight: 600,
		letterSpacing: '0.04em',
		color: 'var(--accent-ink)',
		background: 'var(--accent-soft)',
		padding: '1px 6px',
		borderRadius: 4,
	},
	contactTitle: {
		fontSize: 12,
		color: 'var(--ink-muted)',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	chevron: {
		color: 'var(--ink-faint)',
		transition: 'transform 160ms',
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: 18,
		height: 18,
	},
	contactDetails: {
		marginLeft: 42,
		marginTop: 8,
		display: 'flex',
		flexDirection: 'column',
		gap: 4,
	},
	contactBodyRow: {
		display: 'flex',
		alignItems: 'flex-start',
		justifyContent: 'space-between',
		gap: 12,
	},
	contactDetailsCol: {
		flex: 1,
		minWidth: 0,
		display: 'flex',
		flexDirection: 'column',
		gap: 4,
	},
	detailLine: {
		display: 'flex',
		alignItems: 'center',
		gap: 6,
		fontSize: 12.5,
		color: 'var(--ink)',
		lineHeight: 1.4,
	},
	detailLabel: {
		color: 'var(--ink-muted)',
	},
	detailValue: {
		color: 'var(--ink)',
	},
	copyBtn: {
		width: 20,
		height: 20,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: 4,
		color: 'var(--ink-faint)',
		background: 'transparent',
		border: 'none',
		cursor: 'pointer',
		padding: 0,
		transition: 'background 120ms, color 120ms',
		':hover': {
			background: 'var(--hover)',
			color: 'var(--ink)',
		},
	},
	emailLink: {
		color: 'var(--ink)',
		textDecoration: 'underline',
		fontSize: 12.5,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		minWidth: 0,
	},
	unsubscribedBadge: {
		display: 'inline-flex',
		alignItems: 'center',
		fontSize: 10,
		fontWeight: 600,
		letterSpacing: '0.04em',
		textTransform: 'uppercase',
		padding: '1px 6px',
		borderRadius: 4,
		background: 'rgba(239, 68, 68, 0.12)',
		color: '#EF4444',
	},
	findCellLink: {
		alignSelf: 'flex-start',
		background: 'transparent',
		border: 'none',
		color: 'var(--accent)',
		fontSize: 12.5,
		padding: 0,
		cursor: 'pointer',
		textDecoration: 'underline',
		':hover': {
			color: 'var(--accent-ink)',
		},
		':disabled': {
			color: 'var(--ink-faint)',
			cursor: 'default',
			textDecoration: 'none',
		},
	},
	callBtn: {
		flexShrink: 0,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: 32,
		padding: '0 18px',
		borderRadius: 8,
		background: 'var(--accent)',
		color: 'var(--bg)',
		border: 'none',
		fontSize: 13,
		fontWeight: 600,
		cursor: 'pointer',
		transition: 'filter 120ms',
		':hover': {
			filter: 'brightness(1.05)',
		},
		':disabled': {
			background: 'var(--bg-sunken)',
			color: 'var(--ink-faint)',
			cursor: 'not-allowed',
		},
	},
});
